import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Update as UpdateIcon,
  Schedule as ScheduleIcon,
  Sports as SportsIcon,
  Api as ApiIcon
} from '@mui/icons-material';
import { adminApi } from '../api/adminApi';

const AdminApi = () => {
  const [apiStatus, setApiStatus] = useState(null);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    fetchApiStatus();
    fetchUpcomingGames();
    fetchRecentResults();
  }, []);

  const fetchApiStatus = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getApiStatus();
      setApiStatus(response.data);
    } catch (error) {
      console.error('Error fetching API status:', error);
      showSnackbar('Failed to fetch API status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingGames = async () => {
    try {
      const response = await adminApi.getUpcomingGames(5);
      setUpcomingGames(response.data.games);
    } catch (error) {
      console.error('Error fetching upcoming games:', error);
    }
  };

  const fetchRecentResults = async () => {
    try {
      const response = await adminApi.getRecentResults(5);
      setRecentResults(response.data.games);
    } catch (error) {
      console.error('Error fetching recent results:', error);
    }
  };

  const handleUpdateSchedule = async () => {
    try {
      setUpdateLoading(true);
      const response = await adminApi.updateSchedule();
      showSnackbar(
        `Schedule updated: ${response.data.result.created} created, ${response.data.result.updated} updated`,
        'success'
      );
      fetchApiStatus();
      fetchUpcomingGames();
    } catch (error) {
      console.error('Error updating schedule:', error);
      showSnackbar('Failed to update schedule', 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdateScores = async () => {
    try {
      setUpdateLoading(true);
      const response = await adminApi.updateScores();
      showSnackbar(
        `Live scores updated: ${response.data.result.updated} games updated`,
        'success'
      );
      fetchApiStatus();
      fetchRecentResults();
    } catch (error) {
      console.error('Error updating scores:', error);
      showSnackbar('Failed to update live scores', 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fi-FI');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'primary';
      case 'live': return 'warning';
      case 'finished': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ApiIcon />
        API Management
      </Typography>

      <Grid container spacing={3}>
        {/* API Status Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon />
                API Status
              </Typography>
              
              {apiStatus && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Update: {formatDateTime(apiStatus.lastUpdate)}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Schedule Service: {apiStatus.scheduleService.runningJobs}/{apiStatus.scheduleService.totalJobs} jobs running
                    </Typography>
                    <Typography variant="body2">
                      Upcoming Games: {apiStatus.upcomingGames}
                    </Typography>
                    <Typography variant="body2">
                      Recent Results: {apiStatus.recentResults}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Update Controls */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UpdateIcon />
                Manual Updates
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleUpdateSchedule}
                  disabled={updateLoading}
                  startIcon={updateLoading ? <CircularProgress size={20} /> : <ScheduleIcon />}
                  fullWidth
                >
                  Update Schedule
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleUpdateScores}
                  disabled={updateLoading}
                  startIcon={updateLoading ? <CircularProgress size={20} /> : <SportsIcon />}
                  fullWidth
                >
                  Update Live Scores
                </Button>
                
                <Button
                  variant="text"
                  onClick={fetchApiStatus}
                  startIcon={<RefreshIcon />}
                  fullWidth
                >
                  Refresh Status
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Games */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upcoming Games
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Teams</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {upcomingGames.slice(0, 5).map((game) => (
                      <TableRow key={game._id}>
                        <TableCell>
                          {game.homeTeam?.name} vs {game.awayTeam?.name}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(game.gameTime)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={game.status} 
                            size="small" 
                            color={getStatusColor(game.status)} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Results */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Results
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Teams</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentResults.slice(0, 5).map((game) => (
                      <TableRow key={game._id}>
                        <TableCell>
                          {game.homeTeam?.name} vs {game.awayTeam?.name}
                        </TableCell>
                        <TableCell>
                          {game.homeScore} - {game.awayScore}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(game.gameTime)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Information Card */}
        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="h6" gutterBottom>
              Automated Updates
            </Typography>
            <Typography variant="body2">
              • Schedule updates run daily at 6:00 AM (Helsinki time)
            </Typography>
            <Typography variant="body2">
              • Live scores update every 5 minutes during game hours (17:00-23:00)
            </Typography>
            <Typography variant="body2">
              • Enhanced weekend coverage with updates every 3 minutes (Friday-Sunday)
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
              The system fetches data from multiple sources including official Liiga API, EliteProspects, and community-driven hockey APIs.
              If all external sources fail, the system will generate realistic mock data for the 2025-26 season.
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminApi;
